import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutGrid, FolderKanban, Wand2, Images, Layers, Building2, Scaling, Hammer,
  Boxes, ClipboardList, Calculator, Cable, ShieldCheck, User, Crown, Users,
  LogOut, Menu, X, ShieldAlert, Mail, BookOpen, GitCompare, Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AutoBIMLogo, Wordmark } from "@/components/Brand";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import AdBanner from "@/components/AdBanner";

function navGroups(isAr, isAdmin) {
  const g = [
    {
      title: isAr ? "مساحة العمل" : "Workspace",
      items: [
        { to: "/about", icon: Sparkles, ar: "ما يقدّمه التطبيق", en: "What's inside" },
        { to: "/studio", icon: LayoutGrid, ar: "الاستوديو", en: "Studio" },
        { to: "/design-wizard", icon: Sparkles, ar: "معالج التصميم", en: "Design Wizard" },
        { to: "/projects", icon: FolderKanban, ar: "المشاريع", en: "Projects" },
        { to: "/wizard", icon: Wand2, ar: "المعالج", en: "Wizard" },
        { to: "/variants", icon: Images, ar: "المخططات (10)", en: "Variants" },
        { to: "/templates", icon: Boxes, ar: "القوالب", en: "Templates" },
      ],
    },
    {
      title: isAr ? "المخرجات الهندسية" : "Deliverables",
      items: [
        { to: "/floor-plans", icon: Layers, ar: "المساقط", en: "Floor Plans" },
        { to: "/elevations", icon: Building2, ar: "الواجهات", en: "Elevations" },
        { to: "/section", icon: Scaling, ar: "القطاعات", en: "Section" },
        { to: "/foundation", icon: Hammer, ar: "الأساسات", en: "Foundation" },
        { to: "/materials", icon: Calculator, ar: "الكميات والمواد", en: "Materials / BOQ" },
        { to: "/schedules", icon: ClipboardList, ar: "الجداول", en: "Schedules" },
        { to: "/takeoff", icon: Calculator, ar: "حصر الكميات", en: "Take-off" },
        { to: "/mep-report", icon: Cable, ar: "تقرير MEP", en: "MEP Report" },
        { to: "/code-check", icon: ShieldCheck, ar: "فحص الكود", en: "Code Check" },
        { to: "/code-compare", icon: GitCompare, ar: "مقارنة الأكواد", en: "Code Compare" },
      ],
    },
    {
      title: isAr ? "الحساب" : "Account",
      items: [
        { to: "/account", icon: User, ar: "حسابي", en: "Account" },
        { to: "/upgrade", icon: Crown, ar: "الترقية", en: "Upgrade" },
        { to: "/firm", icon: Users, ar: "المكتب", en: "Firm" },
        { to: "/contact", icon: Mail, ar: "تواصل", en: "Contact" },
        { to: "/methodology", icon: BookOpen, ar: "المنهجية", en: "Methodology" },
      ],
    },
  ];
  if (isAdmin) {
    g.push({
      title: isAr ? "الإدارة" : "Admin",
      items: [{ to: "/admin", icon: ShieldAlert, ar: "لوحة الإدارة", en: "Admin Console" }],
    });
  }
  return g;
}

export default function AppShell() {
  const { user, lang, signOut } = useAuth();
  const isAr = lang === "ar";
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const groups = navGroups(isAr, isAdmin);

  const sidebar = (
    <aside className="abl-scroll flex h-full w-64 shrink-0 flex-col overflow-y-auto border-e border-border bg-popover/80 backdrop-blur">
      <div className="flex items-center gap-2.5 px-4 py-4">
        <AutoBIMLogo size={34} />
        <Wordmark />
      </div>
      <nav className="flex-1 space-y-5 px-2 pb-6">
        {groups.map((grp) => (
          <div key={grp.title}>
            <p className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">{grp.title}</p>
            <div className="space-y-0.5">
              {grp.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-${it.to.replace(/\//g, "")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                      isActive ? "bg-primary/15 text-primary" : "text-foreground/75 hover:bg-white/5 hover:text-foreground"
                    }`
                  }
                >
                  <it.icon size={17} />
                  {isAr ? it.ar : it.en}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden abl-grid-bg">
      <div className="hidden lg:block">{sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 start-0">{sidebar}</div>
        </div>
      )}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between gap-3 border-b border-border bg-popover/60 px-4 py-3 backdrop-blur">
          <div className="flex items-center gap-2">
            <button className="grid h-9 w-9 place-items-center rounded-lg border border-border lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
            <span className="hidden text-sm font-semibold text-muted-foreground sm:block">
              {isAr ? `مرحباً، ${user?.full_name || "مهندس"}` : `Welcome, ${user?.full_name || "Engineer"}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button
              data-testid="signout-btn"
              onClick={() => {
                signOut();
                navigate("/login");
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/60 px-3 py-1.5 text-sm font-semibold hover:border-destructive/60 hover:text-red-400"
            >
              <LogOut size={15} />
              <span className="hidden sm:inline">{isAr ? "خروج" : "Sign out"}</span>
            </button>
          </div>
        </header>
        <main className="abl-scroll flex-1 overflow-y-auto">
          <div className="px-4 pt-3"><AdBanner variant="app" isAr={isAr} /></div>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
